// Password protection, ownership and visibility: the lock screen of a private webtop, the unlock and password
// dialogs, Lock Now, and the Security submenu (Public / Linked / Private, Set or Change Password, owning it).
import { loginAvailable, showLogin, signedIn } from './applets.js';
import { applyChromeColor } from './background.js';
import { toggleAdd } from './add.js';
import { hideMenu, submenu } from './menu.js';
import { render } from './render.js';
import { API, $webtop, webtop, setWebtop } from './state.js';
import { api, confirmDialog, modal, toast } from './ui.js';

// Security submenu (whoever unlocked it: webtop.unlocked): who can see and edit the webtop (a radio set), and the
// password that unlocks it, the signed-in owner's alternative. Lock Now / Unlock sit in the main webtop menu. Linked
// and Private need a password or an owner.
const VISIBILITY = [
  { value: 'public', label: 'Public', title: 'Listed, and anyone can view it; only its owner can edit it (anyone, without a password or an owner)' },
  { value: 'linked', label: 'Linked', title: 'Anyone with the link can view it, only its owner can edit it; not listed' },
  { value: 'private', label: 'Private', title: 'Only its owner can view and edit it' },
];
export function securityMenu() {
  submenu(() => [
    { label: 'Security', cls: 'head' },
    ...VISIBILITY.map((v) => ({ label: v.label, title: v.title, radio: true, checked: webtop.visibility === v.value, stay: true, action: () => setVisibility(v.value) })),
    'sep',
    { label: webtop.locked ? 'Change Password' : 'Set Password', action: setPassword },
    ...ownerItems(),
  ]);
}
// Owning the webtop: its signed-in owner, like anyone with its password, holds its keys. One that has another owner
// shows nothing here (only that owner can give it up).
function ownerItems() {
  if (webtop.mine) return [{ label: 'Give Up Ownership', title: 'Without a password, it becomes public: anyone can edit it', action: giveUp }];
  if (webtop.owned || !loginAvailable()) return [];
  return [{ label: 'Own This Webtop', title: 'You, signed in, and anyone with its password decide who can see and edit it', action: own }];
}
async function own() {
  if (!signedIn() && !(await showLogin())) return;
  try { setWebtop(await api('PUT', `${API}/owner`, { own: true })); render(); toast('This webtop is yours'); } catch (e) { toast(e); }
}
async function giveUp() {
  const ok = await confirmDialog({
    title: 'Give Up Ownership?',
    text: webtop.locked ? 'Only its password will unlock it.'
      : `Anyone will be able to edit this webtop.${webtop.visibility !== 'public' ? ' It will become public.' : ''}`,
    button: 'Give Up', danger: true,
  });
  if (!ok) return;
  try { setWebtop(await api('PUT', `${API}/owner`, { own: false })); render(); toast('Ownership given up'); } catch (e) { toast(e); }
}
async function setVisibility(visibility) {
  if (visibility === webtop.visibility) return;
  try { setWebtop(await api('PATCH', API, { visibility })); render(); } catch (e) { toast(e); }
}

const $unlockDlg = modal(document.getElementById('unlock-dlg'));
const $unlockForm = document.getElementById('unlock-form');
const $unlockErr = document.getElementById('unlock-error');
const $unlockHint = document.getElementById('unlock-hint');
const $unlockPw = document.getElementById('unlock-pw-field');
const $unlockLogin = document.getElementById('unlock-login');
const $unlockSubmit = document.getElementById('unlock-submit');
const $pwDlg = modal(document.getElementById('pw-dlg'));
const $pwForm = document.getElementById('pw-form');
const $pwErr = document.getElementById('pw-error');

// Nothing of a webtop is shown, the lock screen's or this address's (`locked`, for what the two share): the empty page.
function blankScreen() {
  setWebtop(null);
  applyChromeColor(null);
  toggleAdd(false);
  $webtop.className = 'locked';
  $webtop.innerHTML = '';
  $webtop.style.removeProperty('background'); // a webtop opened before this one may have set its colour (client/lib/nav.js)
  $webtop.style.removeProperty('--webtop-h'); // no tiles: back to one screen tall
}
// The address is no webtop's: the page as usual (the notch, the launcher), with a word where the tiles would be.
export function renderNoWebtop() {
  blankScreen();
  const box = document.createElement('div');
  box.className = 'lock-screen';
  box.append(Object.assign(document.createElement('p'), { textContent: 'Nothing here to see.' }));
  $webtop.appendChild(box);
  document.title = 'webtop';
}
// The webtop is locked and nothing is loaded: show a lock screen with a way to open the password dialog.
export function renderLocked() {
  blankScreen();
  const box = document.createElement('div');
  box.className = 'lock-screen';
  box.innerHTML = '<div class="lock-icon">🔒</div><p>This webtop is private.</p>';
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'primary'; b.textContent = 'Unlock';
  b.addEventListener('click', () => showUnlock());
  box.appendChild(b);
  $webtop.appendChild(box);
  document.title = '🔒 webtop';
}
// `how` is the 401's {password, owned} (server/routes/http.js): whether a password opens it, and whether it has an
// owner, who opens it by signing in. Without it (a menu, the lock screen's button) the last one is used, else the
// open webtop's.
let lastHow = null;
export function showUnlock(how) {
  if ($unlockDlg.open) return;
  how = how ?? (webtop ? { password: webtop.locked, owned: webtop.owned } : lastHow) ?? { password: true, owned: false };
  lastHow = how;
  const canSignIn = how.owned && !signedIn() && loginAvailable();
  $unlockPw.hidden = $unlockSubmit.hidden = !how.password;
  $unlockLogin.hidden = !canSignIn;
  $unlockHint.hidden = !how.owned;
  $unlockHint.textContent = canSignIn ? `Its owner can open it by signing in${how.password ? ', or anyone with its password' : ''}.`
    : how.password ? 'Its owner, signed in, or anyone with its password can open it.' : 'Only its owner, signed in, can open it.';
  $unlockErr.hidden = true;
  $unlockForm.reset();
  $unlockDlg.showModal();
}

// Set / change / remove the password (empty = remove). Only reachable while the webtop is unlocked.
function setPassword() {
  $pwErr.hidden = true;
  $pwForm.reset();
  document.getElementById('pw-title').textContent = webtop.locked ? 'Change Password' : 'Set Password';
  document.getElementById('pw-remove').hidden = !webtop.locked;
  $pwDlg.showModal();
}
function pwError(msg) { $pwErr.textContent = msg; $pwErr.hidden = false; }
async function savePassword(password) {
  try {
    setWebtop(await api('PUT', `${API}/password`, { password }));
    $pwDlg.close();
    render();
    toast(password ? 'Password set' : 'Password removed');
  } catch (err) { pwError(err.message); }
}
// Forget the unlock cookie in this browser; the webtop asks for the password again.
// Forget the unlock cookie in this browser. A public or linked webtop stays viewable (read-only); a private one
// shows the lock screen and asks for the password again (api() opens the unlock dialog on the 401).
export async function lockNow() {
  try { await api('POST', `${API}/lock`); } catch (e) { return toast(e); }
  hideMenu();
  try { setWebtop(await api('GET', API)); render(); toast('Locked'); } catch (e) { if (e.locked) renderLocked(); else toast(e); }
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  $unlockForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const password = $unlockForm.elements.password.value;
    if (!password) return;
    try {
      setWebtop(await api('POST', `${API}/unlock`, { password }));
      $unlockDlg.close();
      render();
    } catch (err) {
      if (err.stale) return; // the answer for a webtop that is no longer open: this dialog is another one's now
      $unlockErr.textContent = err.message;
      $unlockErr.hidden = false;
      $unlockForm.elements.password.select();
    }
  });
  $unlockDlg.addEventListener('close', () => { if (!webtop) renderLocked(); });
  $unlockLogin.addEventListener('click', () => { $unlockDlg.close(); showLogin(); });
  $pwForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const { password, confirm } = $pwForm.elements;
    if (!password.value) { pwError('Enter a password, or use Remove Password.'); return password.focus(); }
    if (password.value !== confirm.value) { pwError('The two passwords do not match.'); confirm.select(); return; }
    await savePassword(password.value);
  });
  document.getElementById('pw-remove').addEventListener('click', () => savePassword(''));
}
